/**
 * Phase C (audit v92) — Athlete-side coach proposal card.
 *
 * Renders when athlete has a pending coach proposal (goal.approval_status
 * === 'pending_athlete_review'). Lives in inbox, gets embedded as banner
 * on GoalPage when the current goal is pending.
 *
 * Three actions:
 *   - Accept as-is → POST /accept
 *   - Edit & accept (counter-proposal) → POST /edit-and-accept
 *   - Reject → POST /reject (reason required)
 *
 * Design choices (Linear/Notion-inspired):
 *   - Accept is the *primary* action (largest button, brand color).
 *     Coach is presumed-competent — accepting should feel like the
 *     default path, not a reluctant compromise.
 *   - Edit is *secondary* — counter-proposals are normal but require
 *     thought; modal expands the input space.
 *   - Reject is *tertiary* (ghost button) — final option, requires reason.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, PencilSimple, XCircle, Info } from "@phosphor-icons/react";
import type { GoalInboxItem } from "../goalApi";
import { localeFor } from "../lib/dateLocale";
import { formatRaceDistance } from "../lib/raceDistanceLabel";
import {
  useAcceptCoachProposal,
  useEditAndAcceptProposal,
  useRejectCoachProposal,
} from "../goalApi";

function fmtTime(seconds: number | null): string {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

function parseHmsToSeconds(hms: string): number | null {
  // Accept "H:MM:SS", "M:SS", or raw seconds.
  if (!hms.trim()) return null;
  if (/^\d+$/.test(hms.trim())) return parseInt(hms.trim(), 10);
  const parts = hms.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => isNaN(p) || p < 0)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

export function GoalProposalCard({ proposal }: { proposal: GoalInboxItem }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<"view" | "edit" | "reject">("view");

  // Edit form state — pre-filled with coach's proposed values.
  const [raceDate, setRaceDate] = useState(proposal.race_date || "");
  const [targetTimeStr, setTargetTimeStr] = useState(
    proposal.target_time_seconds ? fmtTime(proposal.target_time_seconds) : "",
  );
  const [counterReason, setCounterReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const accept = useAcceptCoachProposal(proposal.id);
  const reject = useRejectCoachProposal(proposal.id);
  const editAccept = useEditAndAcceptProposal(proposal.id);

  const onAccept = async () => {
    try {
      await accept.mutateAsync();
    } catch (e) {
      // silent: error surfaced via mutation.isError state
    }
  };

  const onSubmitCounter = async () => {
    const newSec = parseHmsToSeconds(targetTimeStr);
    if (counterReason.trim().length < 3) return;
    try {
      await editAccept.mutateAsync({
        race_date: raceDate || null,
        target_time_seconds: newSec,
        reason: counterReason.trim(),
      });
      setMode("view");
    } catch (e) {
      // silent: error surfaced via mutation.isError state
    }
  };

  const onSubmitReject = async () => {
    if (rejectReason.trim().length < 3) return;
    try {
      await reject.mutateAsync({ reason: rejectReason.trim() });
      setMode("view");
    } catch (e) {
      // silent: error surfaced via reject.isError mutation state
    }
  };

  return (
    <div className="rounded-card border border-accent/40 bg-accent-subtle/20 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Info size={16} className="text-accent mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-[14px] font-medium text-fg-primary">
            {t("goalProposal.title", {
              defaultValue: "Coach proposed a goal for you",
            })}
          </div>
          {proposal.latest_proposal_reason && (
            <div className="mt-1 text-[12px] text-fg-secondary italic">
              "{proposal.latest_proposal_reason}"
            </div>
          )}
        </div>
      </div>

      {/* Proposed values summary */}
      <div className="grid grid-cols-2 gap-2 text-[13px] bg-bg-surface rounded-pill p-2">
        <div>
          <div className="text-[11px] text-fg-muted">
            {t("goalProposal.distance", { defaultValue: "Distance" })}
          </div>
          <div className="text-fg-primary">
            {formatRaceDistance(proposal.race_distance, (key, options) =>
              t(key, { ns: "goal", ...options }),
            ) || "—"}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-fg-muted">
            {t("goalProposal.date", { defaultValue: "Date" })}
          </div>
          <div className="text-fg-primary">
            {proposal.race_date
              ? new Date(proposal.race_date).toLocaleDateString(
                  localeFor(i18n.language),
                  { day: "numeric", month: "long", year: "numeric" },
                )
              : "—"}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-fg-muted">
            {t("goalProposal.target", { defaultValue: "Target time" })}
          </div>
          <div className="text-fg-primary font-mono">
            {fmtTime(proposal.target_time_seconds)}
          </div>
        </div>
        {proposal.notes && (
          <div className="col-span-2">
            <div className="text-[11px] text-fg-muted">
              {t("goalProposal.notes", { defaultValue: "Notes" })}
            </div>
            <div className="text-fg-primary">{proposal.notes}</div>
          </div>
        )}
      </div>

      {mode === "view" && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onAccept}
            disabled={accept.isPending}
            className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-10 py-2 px-4 rounded-pill bg-accent text-accent-on font-medium text-center leading-tight hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            <CheckCircle size={16} />
            {t("goalProposal.accept", { defaultValue: "Accept" })}
          </button>
          <button
            type="button"
            onClick={() => setMode("edit")}
            className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-10 py-2 px-4 rounded-pill border border-bg-border text-fg-primary text-center leading-tight hover:bg-bg-raised transition-colors"
          >
            <PencilSimple size={16} />
            {t("goalProposal.edit", { defaultValue: "Edit & accept" })}
          </button>
          <button
            type="button"
            onClick={() => setMode("reject")}
            className="inline-flex items-center justify-center gap-1.5 min-h-10 py-2 px-3 rounded-pill text-fg-muted hover:text-bad hover:bg-bad-subtle/30 transition-colors"
          >
            <XCircle size={14} />
            {t("goalProposal.reject", { defaultValue: "Reject" })}
          </button>
        </div>
      )}

      {mode === "edit" && (
        <div className="space-y-2 border-t border-bg-border pt-3">
          <div className="text-[12px] text-fg-muted">
            {t("goalProposal.counterHint", {
              defaultValue:
                "Adjust values then send back to coach. Coach will review your counter-proposal.",
            })}
          </div>
          <label className="block text-[12px]">
            <span className="text-fg-muted block mb-0.5">
              {t("goalProposal.date", { defaultValue: "Race date" })}
            </span>
            <input
              type="date"
              value={raceDate}
              onChange={(e) => setRaceDate(e.target.value)}
              className="w-full h-9 px-2 rounded-pill border border-bg-border bg-bg-surface text-[13px]"
            />
          </label>
          <label className="block text-[12px]">
            <span className="text-fg-muted block mb-0.5">
              {t("goalProposal.target", { defaultValue: "Target time (H:MM:SS or seconds)" })}
            </span>
            <input
              type="text"
              value={targetTimeStr}
              onChange={(e) => setTargetTimeStr(e.target.value)}
              placeholder="3:30:00"
              className="w-full h-9 px-2 rounded-pill border border-bg-border bg-bg-surface text-[13px] font-mono"
            />
          </label>
          <label className="block text-[12px]">
            <span className="text-fg-muted block mb-0.5">
              {t("goalProposal.reasonLabel", {
                defaultValue: "Reason (min 3 chars) — coach sees this",
              })}
            </span>
            <textarea
              rows={2}
              value={counterReason}
              onChange={(e) => setCounterReason(e.target.value)}
              placeholder={t("goalProposal.reasonPlaceholderEdit", {
                defaultValue: "e.g. feel ready for sub-3:30, want to push",
              })}
              className="w-full px-2 py-1 rounded-pill border border-bg-border bg-bg-surface text-[13px]"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSubmitCounter}
              disabled={editAccept.isPending || counterReason.trim().length < 3}
              className="flex-1 inline-flex items-center justify-center h-9 px-3 rounded-pill bg-accent text-accent-on text-[13px] font-medium disabled:opacity-50"
            >
              {t("goalProposal.sendCounter", {
                defaultValue: "Send counter-proposal",
              })}
            </button>
            <button
              type="button"
              onClick={() => setMode("view")}
              className="inline-flex items-center justify-center h-9 px-3 rounded-pill border border-bg-border text-[13px]"
            >
              {t("goalProposal.cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      )}

      {mode === "reject" && (
        <div className="space-y-2 border-t border-bg-border pt-3">
          <label className="block text-[12px]">
            <span className="text-fg-muted block mb-0.5">
              {t("goalProposal.rejectReasonLabel", {
                defaultValue: "Why are you rejecting? Coach sees this.",
              })}
            </span>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("goalProposal.rejectReasonPlaceholder", {
                defaultValue: "e.g. injury, date conflicts, target unrealistic",
              })}
              className="w-full px-2 py-1 rounded-pill border border-bg-border bg-bg-surface text-[13px]"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSubmitReject}
              disabled={reject.isPending || rejectReason.trim().length < 3}
              className="flex-1 inline-flex items-center justify-center h-9 px-3 rounded-pill bg-bad text-white text-[13px] font-medium disabled:opacity-50"
            >
              {t("goalProposal.confirmReject", {
                defaultValue: "Confirm reject",
              })}
            </button>
            <button
              type="button"
              onClick={() => setMode("view")}
              className="inline-flex items-center justify-center h-9 px-3 rounded-pill border border-bg-border text-[13px]"
            >
              {t("goalProposal.cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
